@if (currentHeroSlide(); as slide) {
  <section class="sav-hero-carousel" aria-label="Featured deals banner">
    <div
      class="sav-hero-slide-bg"
      [attr.data-theme]="slide.theme"
      [attr.data-slide-direction]="heroSlideDirection()"
      [class.hero-slide-bg-animate]="heroSlideAnimate()"
      [style.--hero-image]="slide.imageUrl ? 'url(' + slide.imageUrl + ')' : null"
      (mouseenter)="pauseHeroAutoplay()"
      (mouseleave)="resumeHeroAutoplay()"
    >
      <article class="sav-hero-slide" [class.hero-slide-animate]="heroSlideAnimate()">
        <div class="sav-hero-copy">
          @if (heroTitleHtml()) {
            <!-- The hero headline is the page's only <h1> — it was a role="heading" div before, so
                 the home page shipped with no <h1> at all. -->
            <h1 class="sav-hero-title" [innerHTML]="heroTitleHtml()"></h1>
          }

          @if (slide.descriptionLines.length > 0) {
            <p>
              @for (line of slide.descriptionLines; track $index) {
                {{ line }}
                @if (!$last) {
                  <br />
                }
              }
            </p>
          }

          @if (hasHeroCta(slide)) {
            <a [href]="toLinkHref(slide.ctaLink)" class="sav-hero-cta">{{ slide.ctaLabel }}</a>
          }
        </div>

        @if (hasHeroStorePanel(slide)) {
          <div class="sav-hero-stores">
            @if (slide.storesTitle) {
              <h2>{{ slide.storesTitle }}</h2>
            }
            @if (slide.storesSubtitle) {
              <p>{{ slide.storesSubtitle }}</p>
            }

            @if (slide.badges.length > 0) {
              <div class="sav-store-badges" aria-label="Store partners">
                @for (badge of slide.badges; track badge.alt + badge.logoUrl) {
                  <!-- Store badges route in-app to /<storeSlug>; only admin-authored external
                       links fall back to a plain href. -->
                  @if (badge.path) {
                    <a
                      [routerLink]="badge.path"
                      class="sav-store-badge"
                      [attr.aria-label]="badge.alt"
                    >
                      <ng-container
                        [ngTemplateOutlet]="heroBadgeLogo"
                        [ngTemplateOutletContext]="{ $implicit: badge }"
                      />
                    </a>
                  } @else {
                    <a [href]="badge.href" class="sav-store-badge" [attr.aria-label]="badge.alt">
                      <ng-container
                        [ngTemplateOutlet]="heroBadgeLogo"
                        [ngTemplateOutletContext]="{ $implicit: badge }"
                      />
                    </a>
                  }
                }
              </div>
            }
          </div>
        }
      </article>
      @if (heroSlides().length > 1) {
        <!-- Prev/next arrows. Real <button>s so they are keyboard-reachable; the dots below stay
             as the direct-jump control. -->
        <button
          type="button"
          class="sav-hero-arrow is-prev"
          aria-label="Previous slide"
          (click)="stepHeroSlide(-1)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M15 5 8 12l7 7"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          class="sav-hero-arrow is-next"
          aria-label="Next slide"
          (click)="stepHeroSlide(1)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="m9 5 7 7-7 7"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <div class="sav-carousel-dots" aria-label="Hero carousel pagination">
          @for (slide of heroSlides(); track $index) {
            <span
              [class.active]="currentHeroSlideIndex() === $index"
              role="button"
              tabindex="0"
              [attr.aria-label]="'Go to hero slide ' + ($index + 1)"
              (click)="selectHeroSlide($index)"
              (keydown.enter)="selectHeroSlide($index)"
              (keydown.space)="selectHeroSlide($index); $event.preventDefault()"
            ></span>
          }
        </div>
      }
    </div>
  </section>
}

<!-- Shared by both hero badge anchors (internal routerLink / external href) so the logo
     fallback wiring lives in one place. -->
<ng-template #heroBadgeLogo let-badge>
  <!-- Decorative: the anchor's aria-label carries the name. A logo that fails to load gives way
       to the store name on white. Eager, since the hero is the first thing on the page. -->
  <app-store-logo
    class="sav-store-badge-logo"
    [logoUrl]="badge.logoUrl"
    [name]="badge.alt"
    loading="eager"
    decorative
  />
</ng-template>

<section class="sav-ai-section" aria-label="SavingArena AI prompt">
  <div class="sav-ai-wrap">
    <!-- The name is carried by the SVG wordmark, so the SVG is what gets labelled. A plain <div>
         cannot: `aria-label` is only honoured on an element with a role, and putting it there left
         the wordmark announced as nothing at all. -->
    <div class="sav-ai-title">
      <svg
        width="333"
        height="21"
        viewBox="0 0 333 21"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="SavingArena AI"
      >
        <path
          d="M14.5821 11.54H8.44612C3.32412 11.54 0.750119 9.928 0.750119 6.704V5.69C0.750119 2.44 3.32412 0.854002 8.44612 0.854002H11.0461C11.8781 0.854002 12.0601 1.322 12.0601 2.258C12.0601 3.194 11.8781 3.662 11.0461 3.662H8.36812C5.32612 3.662 4.18212 4.546 4.18212 5.69V6.704C4.18212 7.822 5.32612 8.732 8.36812 8.732H14.5041C19.6261 8.732 22.2001 10.344 22.2001 13.568V14.868C22.2001 18.118 19.6261 19.704 14.5041 19.704H1.76412C0.932119 19.704 0.750119 19.236 0.750119 18.3C0.750119 17.364 0.932119 16.896 1.76412 16.896H14.5821C17.6241 16.896 18.7681 16.012 18.7681 14.868V13.568C18.7681 12.45 17.6241 11.54 14.5821 11.54ZM38.2972 0.776001C39.3112 0.776001 39.9092 1.114 40.3252 1.686L51.6612 17.858C51.7912 18.066 51.8432 18.274 51.8432 18.456C51.8432 19.236 50.8292 19.834 49.9452 19.834C49.4772 19.834 49.0612 19.678 48.8012 19.314L45.7332 14.842H31.2772L28.3912 19.314C28.1572 19.678 27.7152 19.834 27.2732 19.834C26.4152 19.834 25.4012 19.236 25.4012 18.43C25.4012 18.222 25.4792 18.04 25.5832 17.858L36.2432 1.686C36.6332 1.114 37.2572 0.776001 38.2972 0.776001ZM38.2972 3.948L33.0712 12.034H43.8092L38.2972 3.948ZM63.0307 19.808C62.0167 19.808 61.4187 19.496 61.0027 18.898L49.5627 2.726C49.4327 2.518 49.3547 2.336 49.3547 2.128C49.3547 1.374 50.4467 0.750002 51.3567 0.750002C51.7987 0.750002 52.3447 0.906001 52.6047 1.296L63.1087 16.402L73.5087 1.296C73.7427 0.932001 74.1587 0.750002 74.6527 0.750002C75.5367 0.750002 76.6027 1.348 76.6027 2.154C76.6027 2.362 76.5247 2.57 76.3947 2.726L65.0847 18.898C64.6687 19.496 64.0447 19.808 63.0307 19.808ZM84.2016 1.972V18.586C84.2016 19.574 83.6036 19.808 82.4596 19.808C81.3416 19.808 80.7436 19.574 80.7436 18.586V1.972C80.7436 0.984002 81.3416 0.750002 82.4596 0.750002C83.6036 0.750002 84.2016 0.984002 84.2016 1.972ZM110.918 19.808C109.982 19.808 109.774 19.678 108.864 18.924L92.6922 5.3V18.586C92.6922 19.574 92.0942 19.808 90.9502 19.808C89.8322 19.808 89.2342 19.574 89.2342 18.586V1.972C89.2342 1.01 89.8062 0.750002 90.8722 0.750002C92.0162 0.750002 92.5102 1.036 93.3162 1.712L109.254 15.284V1.972C109.254 0.984002 109.852 0.750002 110.97 0.750002C112.114 0.750002 112.712 0.984002 112.712 1.972V18.586C112.712 19.6 112.114 19.808 110.918 19.808ZM140.82 10.24V14.868C140.82 18.17 138.116 19.704 133.098 19.704H125.22C120.098 19.704 117.524 18.092 117.524 14.868V5.69C117.524 2.44 120.098 0.854002 125.22 0.854002H128.886C129.718 0.854002 129.9 1.322 129.9 2.258C129.9 3.194 129.718 3.662 128.886 3.662H125.324C122.282 3.662 120.956 4.546 120.956 5.69V14.868C120.956 15.986 122.282 16.896 125.324 16.896H132.994C136.036 16.896 137.336 15.986 137.336 14.868V10.24C137.336 9.226 137.908 9.018 139.052 9.018C140.196 9.018 140.82 9.226 140.82 10.24ZM156.643 0.776001C157.657 0.776001 158.255 1.114 158.671 1.686L170.007 17.858C170.137 18.066 170.189 18.274 170.189 18.456C170.189 19.236 169.175 19.834 168.291 19.834C167.823 19.834 167.407 19.678 167.147 19.314L164.079 14.842H149.623L146.737 19.314C146.503 19.678 146.061 19.834 145.619 19.834C144.761 19.834 143.747 19.236 143.747 18.43C143.747 18.222 143.825 18.04 143.929 17.858L154.589 1.686C154.979 1.114 155.603 0.776001 156.643 0.776001ZM156.643 3.948L151.417 12.034H162.155L156.643 3.948ZM174.323 2.05C174.323 1.244 174.713 0.854002 175.545 0.854002H190.157C194.863 0.854002 197.801 2.518 197.801 5.066V8.342C197.801 10.89 194.863 12.554 190.157 12.554H189.117C186.049 12.554 184.801 12.528 183.449 12.45V12.502C184.567 12.918 185.763 13.308 189.715 14.894L195.981 17.416C196.579 17.65 196.813 18.014 196.813 18.586C196.813 19.522 196.189 19.808 195.045 19.808C194.551 19.808 193.537 19.756 192.991 19.444L177.781 12.866V18.586C177.781 19.574 177.183 19.808 176.039 19.808C174.921 19.808 174.323 19.574 174.323 18.586V2.05ZM189.845 3.662H177.781V9.798H189.845C193.381 9.798 194.369 9.07 194.369 8.134V5.326C194.369 4.39 193.381 3.662 189.845 3.662ZM222.614 11.826H205.74V14.868C205.74 15.986 207.066 16.896 210.108 16.896H222.536C223.368 16.896 223.55 17.364 223.55 18.3C223.55 19.21 223.368 19.704 222.536 19.704H210.004C204.882 19.704 202.308 18.092 202.308 14.868V5.69C202.308 2.44 204.882 0.854002 210.004 0.854002H213.878C214.684 0.854002 214.866 1.322 214.866 2.258C214.866 3.194 214.684 3.662 213.878 3.662H210.108C207.066 3.662 205.74 4.546 205.74 5.69V9.018H222.614C223.446 9.018 223.628 9.486 223.628 10.422C223.628 11.358 223.446 11.826 222.614 11.826ZM250.008 19.808C249.072 19.808 248.864 19.678 247.954 18.924L231.782 5.3V18.586C231.782 19.574 231.184 19.808 230.04 19.808C228.922 19.808 228.324 19.574 228.324 18.586V1.972C228.324 1.01 228.896 0.750002 229.962 0.750002C231.106 0.750002 231.6 1.036 232.406 1.712L248.344 15.284V1.972C248.344 0.984002 248.942 0.750002 250.06 0.750002C251.204 0.750002 251.802 0.984002 251.802 1.972V18.586C251.802 19.6 251.204 19.808 250.008 19.808ZM268.834 0.776001C269.848 0.776001 270.446 1.114 270.862 1.686L282.198 17.858C282.328 18.066 282.38 18.274 282.38 18.456C282.38 19.236 281.366 19.834 280.482 19.834C280.014 19.834 279.598 19.678 279.338 19.314L276.27 14.842H261.814L258.928 19.314C258.694 19.678 258.252 19.834 257.81 19.834C256.952 19.834 255.938 19.236 255.938 18.43C255.938 18.222 256.016 18.04 256.12 17.858L266.78 1.686C267.17 1.114 267.794 0.776001 268.834 0.776001ZM268.834 3.948L263.608 12.034H274.346L268.834 3.948ZM310.165 0.776001C311.179 0.776001 311.777 1.114 312.193 1.686L323.529 17.858C323.659 18.066 323.711 18.274 323.711 18.456C323.711 19.236 322.697 19.834 321.813 19.834C321.345 19.834 320.929 19.678 320.669 19.314L317.601 14.842H303.145L300.259 19.314C300.025 19.678 299.583 19.834 299.141 19.834C298.283 19.834 297.269 19.236 297.269 18.43C297.269 18.222 297.347 18.04 297.451 17.858L308.111 1.686C308.501 1.114 309.125 0.776001 310.165 0.776001ZM310.165 3.948L304.939 12.034H315.677L310.165 3.948ZM331.303 1.972V18.586C331.303 19.574 330.705 19.808 329.561 19.808C328.443 19.808 327.845 19.574 327.845 18.586V1.972C327.845 0.984002 328.443 0.750002 329.561 0.750002C330.705 0.750002 331.303 0.984002 331.303 1.972Z"
          fill="currentColor"
        />
      </svg>
    </div>

    <!--
      The AI entry point. Visually this is the same pill it has always been — what changed is that
      the static line of copy is now a real field: typing here opens the assistant drawer and starts
      a conversation, which continues in the panel's own input.
    -->
    <form class="sav-ai-prompt" role="search" (submit)="$event.preventDefault(); askAssistant()">
      <span class="sav-ai-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="6" y="6" width="12" height="12" rx="2" />
          <path d="M12 2V5" />
          <path d="M12 19V22" />
          <path d="M2 12H5" />
          <path d="M19 12H22" />
        </svg>
      </span>
      <input
        type="text"
        class="sav-ai-prompt-input"
        [value]="assistantDraft()"
        (input)="onAssistantDraft($any($event.target).value)"
        (focus)="openAssistant()"
        placeholder="Ask about coupons, deals, Products, or stores SAVINGARENA AI finds the best savings."
        aria-label="Ask SavingArena AI about coupons, deals, products or stores"
      />
      <button type="submit" class="sav-ai-prompt-send" aria-label="Ask SavingArena AI">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 12l16-8-6 8 6 8-16-8Z"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </form>
  </div>
</section>

@for (section of topStoresSections(); track section.id) {
  <section class="sav-top-stores-section" aria-label="Event top stores">
    <div class="sav-top-stores-wrap">
      <h2>{{ section.heading }}</h2>
      <p>The stores running the strongest offers in this event</p>

      <div class="sav-top-store-list" appAutoScrollRow>
        @for (store of section.stores; track store.id) {
          <a [routerLink]="store.path" class="sav-top-store" [attr.aria-label]="store.name">
            <app-store-logo
              class="sav-top-store-logo"
              [logoUrl]="store.logoUrl"
              [name]="store.name"
              decorative
            />
          </a>
        }
      </div>
    </div>
  </section>
}

<section class="sav-brand-directory-section" aria-label="Brand directory">
  <div class="sav-brand-directory-wrap">
    @if (brandDirectoryTitle()) {
      <h2 class="sav-brand-directory-title">{{ brandDirectoryTitle() }}</h2>
      <p>Brands we partner with, and the offers they are running right now</p>
    }

    <div class="sav-brand-directory-grid">
      <article class="sav-brand-card sav-add-brand-card">
        <a routerLink="/add-your-brand" aria-label="Add your brand on SavingArena">
          <span class="sav-add-brand-emoji" aria-hidden="true">👋</span>
          <span class="sav-add-brand-arrow" aria-hidden="true"
            ><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M16.0037 9.41421L7.39712 18.0208L5.98291 16.6066L14.5895 8H7.00373V6H18.0037V17H16.0037V9.41421Z"
              ></path></svg
          ></span>
          <h3 class="type-on-dark">Add your brand on SavingArena</h3>
        </a>
      </article>

      @for (store of brandDirectoryStores(); track store.name + store.href) {
        <article class="sav-brand-card">
          <!--
            No `aria-label` on the link. It read "<store> brand", which does not contain the text
            actually inside the link — the name, the domain and the description — so a screen reader
            announced one thing while the page showed another, and voice control could not act on
            what was read out. Without it the name is built from the contents, which is both correct
            and what a sighted visitor sees.

            The logo is `alt=""` for the same reason: the heading right below it already says the
            store's name, so describing the image repeats it.
          -->
          <a [routerLink]="store.href">
            <div
              class="sav-brand-cover"
              [style.background-image]="store.coverUrl ? 'url(' + store.coverUrl + ')' : null"
              [style.background-color]="store.coverBackgroundColor || null"
            ></div>
            <!-- Decorative: the heading right below names the store. The disc, its ring and its
                 pull over the cover's edge all live on `.sav-brand-avatar`, now the slot itself. -->
            <app-store-logo
              class="sav-brand-avatar"
              [logoUrl]="store.logoUrl"
              [name]="store.name"
              decorative
            />
            <h3>{{ store.name }}</h3>
            @if (store.domain) {
              <p>@{{ store.domain }}</p>
            }
            @if (store.description) {
              <p>{{ store.description }}</p>
            }
          </a>
        </article>
      }
    </div>
  </div>
</section>

@if (dealTabs().length > 0) {
  <section class="sav-today-deals-section" aria-label="Today's top deals">
    <div class="sav-today-deals-wrap">
      <h2>Today’s Top Deals You Should Check Out</h2>
      <p>The best offers from the stores we track, refreshed every day</p>

      <nav class="sav-today-deals-tabs" aria-label="Deal stores" role="tablist">
        @for (tab of dealTabs(); track tab.id) {
          <button
            type="button"
            role="tab"
            [id]="'deals-tab-' + tab.id"
            [class.active]="selectedTabId() === tab.id"
            [attr.aria-selected]="selectedTabId() === tab.id"
            [attr.aria-controls]="'deals-panel-' + tab.id"
            (click)="selectTab(tab.id)"
          >
            {{ tab.label }}
          </button>
        }
      </nav>

      <div class="sav-today-deals-brand">
        <span class="sav-today-deals-brand-logo">
          <!-- The heading beside it says whose deals these are, so the mark is decorative. A logo
               that fails to load gives way to the brand's name on white. -->
          <app-store-logo
            [logoUrl]="selectedTab()?.logoUrl || ''"
            [name]="selectedTab()?.label || 'Store'"
            decorative
          />

        </span>
        <div>
          <h3>Today’s Top Deals</h3>
          <p>PRESENTED BY {{ selectedTab()?.presentedBy }}</p>
        </div>
      </div>

      <div
        class="sav-product-grid sav-today-deals-cards"
        role="tabpanel"
        [id]="'deals-panel-' + selectedTabId()"
        [attr.aria-labelledby]="'deals-tab-' + selectedTabId()"
      >
        @for (deal of visibleDeals(); track deal.title + $index) {
          <!-- `save` is intentionally not passed: the card derives "SAVE $8.00" from oldPrice and
             price, exactly as it does for the New Collection grid. -->
          <app-product-card
            [title]="deal.title"
            [image]="deal.imageUrl"
            [hoverImage]="deal.hoverImageUrl"
            [storeName]="selectedTab()?.presentedBy || ''"
            [storeLogoUrl]="selectedTab()?.logoUrl || ''"
            [price]="deal.price"
            [oldPrice]="deal.oldPrice"
            [external]="deal.isExternal"
            [detailHref]="deal.detailPath"
            [productId]="deal.id"
          />
        }
      </div>

      <!-- Pager only when there is genuinely more than one slide; a lone dot is just noise. -->
      @if (totalSlides() > 1) {
        <div class="sav-today-deals-dots">
          @for (index of slideIndexes(); track index) {
            <button
              type="button"
              [class.active]="currentSlide() === index"
              [attr.aria-label]="'Go to deals slide ' + (index + 1)"
              (click)="selectSlide(index)"
            ></button>
          }
        </div>
      }

      <!-- Scoped to the selected store, not the whole site. The deals link is dropped when that
         store has too few products to have a deals page. -->
      <div class="sav-today-deals-actions">
        @if (selectedTab()?.dealsPath) {
          <a [routerLink]="selectedTab()?.dealsPath">View All {{ selectedTab()?.label }} Deals</a>
        }
        @if (selectedTab()?.couponsPath) {
          <a [routerLink]="selectedTab()?.couponsPath"
            >View All {{ selectedTab()?.label }} Coupons</a
          >
        }
      </div>
    </div>
  </section>
}

@if (sponsoredBrands().length > 0) {
  <section class="sav-sponsored-brands-section" aria-label="Sponsored brands">
    <div class="sav-sponsored-brands-wrap">
      <h2>
        {{ sponsoredBrandsTitle() || 'Sponsored Brands You Should Check Out' }}
        @if (isStaticSection('sponsoredBrands')) {
          <span
            class="sav-static-section-dot"
            aria-label="Static section not yet API-driven"
          ></span>
        }
      </h2>
      <p>Featured partners and the campaigns they are running this season</p>

      <div class="sav-sponsored-brands-grid">
        @for (brand of sponsoredBrands(); track brand.title + brand.storeName) {
          <article class="sav-sponsored-card">
            <div
              class="sav-sponsored-media"
              [style.background-image]="brand.imageUrl ? 'url(' + brand.imageUrl + ')' : null"
              [attr.aria-label]="brand.title"
            >
              <a
                [routerLink]="brand.storePath"
                class="sav-sponsored-store-badge"
                [attr.aria-label]="brand.storeName"
              >
                <!-- Decorative: the anchor's aria-label names the store. -->
                <app-store-logo
                  class="sav-sponsored-store-logo"
                  [logoUrl]="brand.storeLogoUrl || ''"
                  [name]="brand.storeName"
                  decorative
                />
              </a>
            </div>
            <div class="sav-sponsored-card-body">
              <h3>{{ brand.title }}</h3>
              <p>{{ brand.description }}</p>
            </div>
          </article>
        }
      </div>
    </div>
  </section>
}

@if (hasPopularCategories()) {
<section class="sav-popular-categories-section" aria-label="Popular categories">
  <div class="sav-popular-categories-wrap">
    <!-- No static marker: both tabs are API-fed now — "Coupons & Offers" from /popular-categories
         and "Products & Deals" from /deal-categories. A tab with no data is dropped rather than
         backfilled with placeholders, and with no tabs at all the whole section stays out of the
         page instead of leaving a heading over an empty grid. -->
    <h2>Popular Categories</h2>
    <p>The categories shoppers browse most on SavingArena</p>

    <div class="sav-popular-categories-tabs" role="tablist" aria-label="Category feeds">
      @for (tab of popularCategoryTabs(); track tab.id) {
        <button
          type="button"
          role="tab"
          [id]="'popular-categories-tab-' + tab.id"
          [class.active]="selectedPopularCategoryTabId() === tab.id"
          [attr.aria-selected]="selectedPopularCategoryTabId() === tab.id"
          [attr.aria-controls]="'popular-categories-panel-' + tab.id"
          (click)="selectPopularCategoryTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      }
    </div>

    <div
      class="sav-popular-categories-grid"
      role="tabpanel"
      [id]="'popular-categories-panel-' + selectedPopularCategoryTabId()"
      [attr.aria-labelledby]="'popular-categories-tab-' + selectedPopularCategoryTabId()"
    >
      @for (category of visiblePopularCategories(); track category.label + $index) {
        <a
          class="sav-popular-category-card"
          [class]="'sav-popular-category-card ' + (category.thumb || '')"
          [routerLink]="category.path || null"
        >
          <span class="sav-popular-category-thumb" aria-hidden="true">
            @if (category.imageUrl) {
              <img [src]="category.imageUrl" [alt]="category.label" loading="lazy" />
            } @else {
              <!-- No artwork uploaded for this category. `iconGlyph` is the editor's icon where the
                   storefront can paint it, and the category's initials where it cannot, so the
                   circle is never blank. -->
              <span class="sav-popular-category-icon">{{ category.iconGlyph }}</span>
            }
          </span>
          <span class="sav-popular-category-label">{{ category.label }}</span>
        </a>
      }
    </div>

    <!-- Not `aria-hidden`. These are real buttons that move the carousel and they take keyboard
         focus, so hiding the row from assistive technology left a keyboard user tabbing onto
         controls their screen reader would not announce. Each button already carries its own
         label. -->
    <div class="sav-popular-categories-dots">
      @for (index of popularCategorySlideIndexes(); track index) {
        <button
          type="button"
          [class.active]="activePopularCategorySlide() === index"
          [attr.aria-label]="'Go to category slide ' + (index + 1)"
          (click)="selectPopularCategorySlide(index)"
        ></button>
      }
    </div>
  </div>
</section>
}

@if (trendingCouponTabs().length > 0) {
  <section class="sav-trending-coupons-section" aria-label="Trending coupons">
    <div class="sav-trending-coupons-wrap">
      <h2>Trending Coupons</h2>
      <p>Verified codes and offers shoppers are using right now</p>

      <div class="sav-trending-coupons-tabs">
        @for (tab of trendingCouponTabs(); track tab.id) {
          <button
            type="button"
            [class.active]="selectedTrendingCouponTabId() === tab.id"
            (click)="selectTrendingCouponTab(tab.id)"
          >
            {{ tab.label }}
          </button>
        }
      </div>

      <div class="sav-trending-coupons-grid">
        @for (coupon of visibleTrendingCoupons(); track coupon.store + coupon.title) {
          <article class="sav-trending-coupon-card">
            <a
              class="sav-trending-coupon-brand"
              [routerLink]="coupon.storePath || null"
              [attr.aria-label]="coupon.store"
            >
              <!-- `.sav-trending-coupon-brand-logo` stays on the slot: that global rule sizes it
                   and draws the ring. Decorative because the link's aria-label names the store. -->
              <app-store-logo
                class="sav-trending-coupon-brand-logo"
                [logoUrl]="coupon.logoUrl"
                [name]="coupon.store"
                decorative
              />
            </a>
            <div class="sav-trending-coupon-content">
              <!-- Chip + hairline rule + verified, sitting under the logo panel. The chip label is
                 derived from the existing ctaType — no new data. -->
              <div class="sav-trending-coupon-top-row">
                <span class="sav-trending-coupon-type">{{
                  coupon.ctaType === 'code' ? 'Code' : 'Deal'
                }}</span>
                <span class="sav-trending-coupon-verified">Verified</span>
              </div>
              <a class="sav-trending-coupon-store" [routerLink]="coupon.storePath || null">{{
                coupon.store
              }}</a>
              <!-- Reads as body copy, not a heading: `.type-inherit` is the design system's opt-out
                 so the size comes from the card instead of the heading scale. -->
              <h3 class="type-inherit">{{ coupon.title }}</h3>
              <div class="sav-trending-coupon-bottom-row">
                <span>Expires : {{ coupon.expires }}</span>
                <!--
                Real hrefs, not `href="#"`: the store's offer URL is a genuine page, so the link is
                middle-clickable and copyable, and it still works with JS off. The handler then does
                the two-tab dance (see `openTrendingCoupon`) and cancels the plain navigation.
              -->
                @if (coupon.ctaType === 'code') {
                  <a
                    [href]="coupon.storePath + '/coupon/' + coupon.id"
                    (click)="openTrendingCoupon(coupon, $event)"
                    ><span class="sav-trending-coupon-code-label">Reveal Code</span></a
                  >
                } @else {
                  <a
                    class="deal"
                    [href]="coupon.storePath + '/coupon/' + coupon.id"
                    (click)="openTrendingCoupon(coupon, $event)"
                    >Get Deal</a
                  >
                }
              </div>
            </div>
          </article>
        }
      </div>

      <!-- Opens the selected tab's category page; hidden when that category has no slug. -->
      @if (trendingMorePath()) {
        <div class="sav-trending-coupons-more">
          <a [routerLink]="trendingMorePath()"
            ><span>{{ trendingMoreLabel() }}</span
            ><svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
              ></path></svg
          ></a>
        </div>
      }
    </div>
  </section>
}

@if (premiumCollectionTabs().length > 0) {
  <section class="sav-premium-collection-section" aria-label="Premium product collection">
    <div class="sav-premium-collection-wrap">
      <header class="sav-premium-collection-header">
        <h2 class="sav-premium-collection-title">
          New Collection
          @if (isStaticSection('premiumCollection')) {
            <span
              class="sav-static-section-dot"
              aria-label="Static section not yet API-driven"
            ></span>
          }
        </h2>
        <p>The newest products added across the stores we track</p>

        <div class="sav-premium-collection-tabs" role="tablist" aria-label="Collection categories">
          @for (tab of premiumCollectionTabs(); track tab.id) {
            <button
              type="button"
              role="tab"
              [id]="'premium-collection-tab-' + tab.id"
              [class.active]="selectedPremiumCollectionTabId() === tab.id"
              [attr.aria-selected]="selectedPremiumCollectionTabId() === tab.id"
              [attr.aria-controls]="'premium-collection-panel-' + tab.id"
              (click)="selectPremiumCollectionTab(tab.id)"
            >
              {{ tab.label }}
            </button>
          }
        </div>
      </header>

      <div
        class="sav-product-grid"
        role="tabpanel"
        [id]="'premium-collection-panel-' + selectedPremiumCollectionTabId()"
        [attr.aria-labelledby]="'premium-collection-tab-' + selectedPremiumCollectionTabId()"
      >
        <!-- Track by id. `title + store` is not unique: a store's product feed carries variants of
             the same item (sizes, colours) under one title, so two cards in a category served by a
             single store collided on the same key and Angular dropped the duplicates (NG0955). -->
        @for (product of premiumCollectionItems(); track product.id) {
          <app-product-card
            [title]="product.title"
            [image]="product.imageUrl"
            [hoverImage]="product.hoverImageUrl"
            [storeName]="product.store"
            [storeLogoUrl]="product.storeLogoUrl || ''"
            [price]="product.price"
            [oldPrice]="product.oldPrice || ''"
            [href]="product.storePath"
            [external]="product.isExternal"
            [detailHref]="product.detailPath"
            [productId]="product.id"
          />
        }
      </div>
    </div>
  </section>
}

<!--
  Hidden until something has actually been published. The section now lists stores by when a person
  made them live, and nothing carries that date until the first publish after the field was added —
  so on day one there is genuinely nothing to show, and an empty grid says less than no grid.
-->
@if (partneredStores().length > 0) {
  <section class="sav-trending-stores-section" aria-label="Trending stores you should check out">
  <div class="sav-trending-stores-wrap">
    <h2>
      Recently Partnered Stores You Should Check Out
      @if (isStaticSection('partneredStores')) {
        <span class="sav-static-section-dot" aria-label="Static section not yet API-driven"></span>
      }
    </h2>
    <p>The latest brands to join SavingArena</p>

    <div class="sav-trending-stores-grid">
      @for (store of partneredStores(); track store.name + $index) {
        <a
          class="sav-trending-store-card"
          [routerLink]="store.path"
        >
          <span class="sav-store-tile-logo-box">
            <!-- The caption below names the store. A logo that fails to load — a dead path, or a
                 favicon lookup that returns nothing — gives way to the name on white. -->
            <app-store-logo [logoUrl]="store.logoUrl" [name]="store.name" decorative />
          </span>
          <span class="sav-store-tile-divider" aria-hidden="true"></span>
          <span class="sav-store-tile-store-meta">
            <span class="sav-store-tile-brand-name">{{ store.name }}</span>
            @if (store.couponsAndDealsCount) {
              <span class="sav-store-tile-count">{{ store.couponsAndDealsCount }}</span>
            }
          </span>
        </a>
      }
    </div>
  </div>
</section>
}

<!-- Shared banner: its promo links follow the featured event. Was an inline copy of the same markup
     with a hardcoded "Black Friday Deals" link. -->
<app-big-sale-banner />

<!-- No category tabs here: the home page shows the latest posts straight. -->
<app-latest-insights [showTabs]="false" />

@if (homeFaqs().length > 0) {
  <section class="sav-faq-section" aria-label="Frequently Asked Questions">
    <div class="sav-faq-wrap">
      <h2>{{ homeFaqTitle() || 'Frequently Asked Questions' }}</h2>
      <p>What shoppers ask most about using SavingArena</p>
      <div class="sav-faq-list">
        @for (faq of homeFaqs(); track $index) {
          <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === $index">
            <button
              class="sav-faq-question"
              type="button"
              [attr.aria-expanded]="expandedFaqIndex() === $index"
              (click)="toggleFaq($index)"
            >
              <span>{{ faq.question }}</span>
              <svg
                class="sav-faq-chevron"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === $index">
              <div class="sav-faq-answer-body" [innerHTML]="faq.answer | trustedCmsHtml"></div>
            </div>
          </div>
        }
      </div>
    </div>
  </section>
}
